import React, { useMemo, useState } from "react";
import { Mafs, Coordinates, Point, Plot, Theme, Line as MafsLine } from "mafs";
import { Card, Space, Typography, Segmented, Tag, Alert, Row, Divider, Statistic } from "antd";
import { UserOutlined, CheckCircleOutlined, BulbOutlined, FallOutlined, RiseOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

export const CurveFitting = ({ config, content }) => {
    const cfg = config || {};
    const explanation = content?.explanation || "Explore regression models and goodness of fit.";

    const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
    const safeNum = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

    const dp = Number.isInteger(cfg.precisionDp) ? clamp(cfg.precisionDp, 0, 4) : 2;
    const format = (n) => {
        const x = safeNum(n, 0);
        return Number(x.toFixed(dp));
    };

    const fitType = (cfg.fitType || "LINEAR").toUpperCase();
    const requiredLen = fitType === "QUADRATIC" ? 3 : 2;

    const points = Array.isArray(cfg.points)
        ? cfg.points
            .filter(Boolean)
            .map((p) => ({ x: safeNum(p.x, 0), y: safeNum(p.y, 0) }))
        : [];

    const safeCoeff = (list, len, defaults) => {
        const inArr = Array.isArray(list) ? list : [];
        const out = [];
        for (let i = 0; i < len; i++) out.push(safeNum(inArr[i], defaults[i]));
        return out;
    };

    const correctDefaults =
        fitType === "QUADRATIC" ? [0, 1, 0] : [1, 0];

    const correctC = safeCoeff(cfg.correctCoefficients, requiredLen, correctDefaults);
    const studentC0 = Array.isArray(cfg.studentCoefficients) ? cfg.studentCoefficients : null;

    const hasStudent =
        Array.isArray(studentC0) &&
        studentC0.length >= requiredLen &&
        studentC0.slice(0, requiredLen).every((v) => Number.isFinite(Number(v)));

    const studentC = hasStudent ? safeCoeff(studentC0, requiredLen, correctC) : null;

    const [viewMode, setViewMode] = useState("CORRECT");
    const active = viewMode === "STUDENT" && hasStudent ? studentC : correctC;

    const evalY = (x, c) => {
        if (fitType === "QUADRATIC") {
            const [a, b, k] = c;
            return a * x * x + b * x + k;
        }
        if (fitType === "EXPONENTIAL") {
            const [a, b] = c;
            const exp = Math.exp(clamp(b * x, -20, 20));
            const y = a * exp;
            return Number.isFinite(y) ? y : 0;
        }
        // LINEAR
        const [m, k] = c;
        return m * x + k;
    };

    const rmse = useMemo(() => {
        const calc = (c) => {
            if (!points.length) return 0;
            const errors = points.map((p) => {
                const yHat = evalY(p.x, c);
                const diff = p.y - yHat;
                return diff * diff;
            });
            const mean = errors.reduce((a, b) => a + b, 0) / points.length;
            return Math.sqrt(mean);
        };

        return {
            correct: calc(correctC),
            student: hasStudent ? calc(studentC) : null,
        };
    }, [points, fitType, hasStudent, correctC, studentC]);

    // Viewbox safety
    const xMin = safeNum(cfg.rangeXMin, 0);
    const xMax = safeNum(cfg.rangeXMax, 10);
    const yMin = safeNum(cfg.rangeYMin, 0);
    const yMax = safeNum(cfg.rangeYMax, 10);

    const safeX = xMax > xMin ? [xMin, xMax] : [0, 10];
    const safeY = yMax > yMin ? [yMin, yMax] : [0, 10];

    const equationText = () => {
        if (fitType === "QUADRATIC") {
            const [a, b, k] = active;
            return `y = ${format(a)}x² + ${format(b)}x + ${format(k)}`;
        }
        if (fitType === "EXPONENTIAL") {
            const [a, b] = active;
            return `y = ${format(a)} · e^(${format(b)}x)`;
        }
        const [m, k] = active;
        return `y = ${format(m)}x + ${format(k)}`;
    };

    const coeffRows = () => {
        if (fitType === "QUADRATIC") {
            return (
                <>
                    <DeltaRow label="a" cur={studentC?.[0]} prev={correctC[0]} format={format} show={hasStudent} />
                    <DeltaRow label="b" cur={studentC?.[1]} prev={correctC[1]} format={format} show={hasStudent} />
                    <DeltaRow label="c" cur={studentC?.[2]} prev={correctC[2]} format={format} show={hasStudent} />
                </>
            );
        }
        if (fitType === "EXPONENTIAL") {
            return (
                <>
                    <DeltaRow label="a" cur={studentC?.[0]} prev={correctC[0]} format={format} show={hasStudent} />
                    <DeltaRow label="b" cur={studentC?.[1]} prev={correctC[1]} format={format} show={hasStudent} />
                </>
            );
        }
        return (
            <>
                <DeltaRow label="Slope (m)" cur={studentC?.[0]} prev={correctC[0]} format={format} show={hasStudent} />
                <DeltaRow label="Intercept (c)" cur={studentC?.[1]} prev={correctC[1]} format={format} show={hasStudent} />
            </>
        );
    };

    return (
        <div className="ch-curve-fit-widget animate-slide-up">
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                <Card size="small" className="ch-shapeshifter-header">
                    <Row justify="space-between" align="middle">
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>
                                Regression Explorer
                            </Title>
                            {hasStudent && (
                                <Segmented
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        { label: "Correct", value: "CORRECT", icon: <CheckCircleOutlined /> },
                                        { label: "Yours", value: "STUDENT", icon: <UserOutlined /> },
                                    ]}
                                />
                            )}
                        </Space>
                        <Tag color="geekblue">{fitType}</Tag>
                    </Row>
                </Card>

                <Text type="secondary">{explanation}</Text>

                <div className="ch-statistics-layout">
                    <div className="ch-statistics-stage">
                        <Mafs height={400} viewBox={{ x: safeX, y: safeY }}>
                            <Coordinates.Cartesian />

                            {/* Points + residuals */}
                            {points.map((p, i) => (
                                <React.Fragment key={i}>
                                    <Point x={p.x} y={p.y} color={Theme.blue} />
                                    {cfg.showResiduals && (
                                        <MafsLine.Segment
                                            point1={[p.x, p.y]}
                                            point2={[p.x, evalY(p.x, active)]}
                                            color={Theme.red}
                                            opacity={0.35}
                                            weight={1}
                                        />
                                    )}
                                </React.Fragment>
                            ))}

                            {/* Fitted curve */}
                            <Plot.OfX
                                y={(x) => evalY(x, active)}
                                color={viewMode === "STUDENT" ? "#fa8c16" : "#52c41a"}
                                weight={4}
                            />
                        </Mafs>
                    </div>

                    <div className="ch-statistics-panel">
                        <Space orientation="vertical" style={{ width: "100%" }}>
                            <Card size="small" title="Model Equation">
                                <div className="ch-fraction-metrics">
                                    <div className="ch-fraction-metric-card">
                                        <Text type="secondary">{fitType} Fit</Text>
                                        <div className="ch-fraction-math">{equationText()}</div>
                                    </div>
                                </div>
                            </Card>

                            {hasStudent && (
                                <Card size="small" title="Regression Delta" className="ch-delta-card">
                                    {coeffRows()}
                                    <Divider style={{ margin: "8px 0" }} />
                                    <Statistic title="RMSE (Correct)" value={rmse.correct} precision={dp} />
                                    <Statistic
                                        title="RMSE (Yours)"
                                        value={rmse.student}
                                        precision={dp}
                                        valueStyle={{
                                            color: rmse.student <= rmse.correct + 0.05 ? "#52c41a" : "#fa8c16",
                                            fontSize: "18px",
                                        }}
                                    />
                                </Card>
                            )}

                            <Alert
                                message="Statistical Tip"
                                description="Residuals (red lines) show the vertical distance between data points and the model. A better fit typically reduces RMSE."
                                type="info"
                                showIcon
                                icon={<BulbOutlined />}
                            />
                        </Space>
                    </div>
                </div>
            </Space>
        </div>
    );
};

const DeltaRow = ({ label, cur, prev, format, show }) => {
    if (!show) return null;

    const c = Number(cur);
    const p = Number(prev);
    const diff = c - p;
    const isIncrease = diff > 0;

    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <Text type="secondary">{label}</Text>
            <Space>
                <Text strong>{format(c)}</Text>
                {Number.isFinite(diff) && Math.abs(diff) > 0.001 && (
                    <Tag
                        color={isIncrease ? "orange" : "cyan"}
                        icon={isIncrease ? <RiseOutlined /> : <FallOutlined />}
                        style={{ margin: 0 }}
                    >
                        {isIncrease ? "+" : ""}
                        {format(diff)}
                    </Tag>
                )}
            </Space>
        </div>
    );
};
